// TXT 렌더러 — 청크 렌더링 + global offset 단일 좌표계 (기획서 0.6, 0.7, 8.2 전체)
//
// 설계 핵심 (기획서가 가장 먼저 구현하라고 강조한 부분):
//  - 위치의 유일한 진실은 globalOffset (책 전체 NFC 텍스트 기준 글자 인덱스).
//  - scrollLeft / chunkIndex / 페이지 번호는 전부 globalOffset 에서 파생되는 보조값.
//  - 모든 위치 이동은 단일 진입점 goToOffset(globalOffset) 하나만 거친다. 우회 금지.
//  - 페이지 이동은 scrollLeft 만 사용. transform:translateX 금지 (Range API 좌표 충돌).
//  - 현재±1 청크, 최대 3청크만 DOM 유지. anchor 기반 청크 교체로 깜빡임/튐 방지.

import { buildBlocks, findBlockByOffset } from '../core/txt-parse.js';
import { buildChunks, findChunkByOffset } from '../core/chunks.js';

export class TxtReader {
  /**
   * @param {HTMLElement} container  본문 컨테이너 (.txt-content). 패딩은 바깥 wrapper 가 담당.
   */
  constructor(container) {
    this.container = container;
    this.blocks = [];
    this.chunks = [];
    this.totalChars = 0;

    // 현재 DOM 에 올라온 윈도우 = [windowFrom, windowTo] 청크 범위
    this.windowFrom = -1;
    this.windowTo = -1;

    this.pageWidth = 0; // 한 페이지 폭 (= 본문 영역 폭, 정수)
    this.columnHeight = 0; // 실측 column 높이 (정수)

    this._onPositionChange = null; // (globalOffset, percent) => void
  }

  // 파일 로드 직후 1회. NFC 정규화된 텍스트를 받아 Block/Chunk 배열 구성 (기획서 8.2.0/8.2.1).
  setText(normalizedText) {
    const { blocks, totalChars } = buildBlocks(normalizedText);
    this.blocks = blocks;
    this.totalChars = totalChars;
    this.chunks = buildChunks(blocks);
    this.windowFrom = -1;
    this.windowTo = -1;
  }

  onPositionChange(fn) {
    this._onPositionChange = fn;
  }

  // ───────────────────────────── 레이아웃 측정 ─────────────────────────────

  // 기획서 5.5: 근사 계산 후 반드시 실측값 사용. 소수점 픽셀은 Math.floor 정수화.
  /**
   * 레이아웃 측정 + column 폭 강제 (기획서 5.5, 8.2.2).
   *
   * 핵심 수정: "한 페이지 = 정확히 한 column" 을 보장한다.
   * 이전 구현은 column-width 를 px 희망값으로 줘서 브라우저가 칸 개수를
   * 자체 판단 → 한 화면에 2칸이 생기면 scrollLeft 단위(1칸)와 어긋나
   * 페이지가 건너뛰었다. 이를 막기 위해:
   *   - 스크롤 단위 pageWidth = 컨테이너 clientWidth (패딩 제외 실제 폭) 하나로 단일화
   *   - column-width 대신 column-count:1 + 각 page 폭을 pageWidth 로 고정
   *   - 단, 다단(여러 페이지)을 위해 컨텐츠는 가로로 흐르되 한 칸 = pageWidth
   * @param {object} styleOpts { fontSize, lineHeight, fontFamilyVar }
   */
  measure(styleOpts) {
    const cs = getComputedStyle(this.container);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const padT = parseFloat(cs.paddingTop) || 0;
    const padB = parseFloat(cs.paddingBottom) || 0;

    // clientWidth/clientHeight 는 스크롤바·border 제외, 패딩 포함값.
    // 패딩까지 뺀 "콘텐츠 실폭"을 단일 기준으로 삼는다 (스크롤 단위와 동일).
    const contentW = Math.max(1, Math.floor(this.container.clientWidth - padL - padR));
    const contentH = Math.max(1, Math.floor(this.container.clientHeight - padT - padB));

    // ★수정(빈 화면 회귀 방지): 측정 시점에 레이아웃이 아직 안 잡혀
    // 높이가 비정상(<40px)으로 나오면 그 값을 적용하지 않는다.
    // 이전 구현은 1px 을 height 인라인으로 박아 영구히 찌그러졌다.
    // 호출부가 다음 프레임에 재호출하도록 ok:false 를 반환한다.
    if (contentH < 40 || contentW < 40) {
      return { ok: false, columnHeight: contentH, pageWidth: contentW };
    }

    this.pageWidth = contentW;
    this.columnHeight = contentH;

    if (styleOpts) {
      this.container.style.fontSize = styleOpts.fontSize + 'px';
      this.container.style.lineHeight = String(styleOpts.lineHeight);
    }
    // column 폭만 강제. ★height 는 인라인으로 박지 않는다 —
    // CSS 높이 체인(.content-inner→.reader-mount→.txt-content:100%)이
    // 채우게 둔다. 여기서 px 로 박으면 측정 오차가 영구 고정된다.
    this.container.style.columnWidth = contentW + 'px';
    this.container.style.columnGap = '0px';
    this.container.style.columnFill = 'auto';
    void this.container.offsetWidth; // 강제 reflow

    return { ok: true, columnHeight: this.columnHeight, pageWidth: this.pageWidth };
  }

  // 최후의 수단: 측정값이 끝내 비정상이어도 일단 적용해 영구 공백을 막는다.
  // (정상적으로는 _measureWhenReady 재시도로 ok:true 가 나야 한다)
  _forceMeasure(styleOpts) {
    const cs = getComputedStyle(this.container);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const w = Math.max(280, Math.floor(this.container.clientWidth - padL - padR));
    const h = Math.max(
      320,
      Math.floor(this.container.clientHeight) || Math.floor(window.innerHeight * 0.7)
    );
    this.pageWidth = w;
    this.columnHeight = h;
    if (styleOpts) {
      this.container.style.fontSize = styleOpts.fontSize + 'px';
      this.container.style.lineHeight = String(styleOpts.lineHeight);
    }
    this.container.style.columnWidth = w + 'px';
    this.container.style.columnGap = '0px';
    this.container.style.columnFill = 'auto';
    void this.container.offsetWidth;
    return { ok: true, columnHeight: h, pageWidth: w };
  }

  getPageWidth() {
    return this.pageWidth;
  }

  // ───────────────────────────── 청크 윈도우 렌더링 ─────────────────────────────

  _windowRangeFor(chunkIndex) {
    const from = Math.max(0, chunkIndex - 1);
    const to = Math.min(this.chunks.length - 1, chunkIndex + 1);
    return [from, to];
  }

  _isWindowRendered(chunkIndex) {
    if (this.windowFrom < 0) return false;
    const [from, to] = this._windowRangeFor(chunkIndex);
    return this.windowFrom === from && this.windowTo === to;
  }

  /**
   * 윈도우(chunkIndex-1 .. chunkIndex+1) 청크들을 DOM 에 구성.
   * 각 block 을 <p data-idx data-start> 로 렌더. 그 외 청크의 <p> 는 제거.
   * 이벤트 리스너는 개별 <p> 에 절대 부착하지 않음 (기획서 8.4.1 — 위임만).
   */
  renderWindow(chunkIndex) {
    const [from, to] = this._windowRangeFor(chunkIndex);
    if (this.windowFrom === from && this.windowTo === to) return;

    const blockFrom = this.chunks[from].blockFrom;
    const blockTo = this.chunks[to].blockTo;

    // innerHTML 일괄 교체 → 제거된 노드 참조가 끊겨 GC 유도 (기획서 8.4.1)
    const parts = new Array(blockTo - blockFrom + 1);
    for (let i = blockFrom; i <= blockTo; i++) {
      const b = this.blocks[i];
      parts[i - blockFrom] =
        `<p data-idx="${b.index}" data-start="${b.start}">${escapeHtml(b.text)}</p>`;
    }
    this.container.innerHTML = parts.join('');
    this.windowFrom = from;
    this.windowTo = to;

    // 강제 reflow 로 column 레이아웃 계산 완료 보장 (기획서 8.2.4 3단계)
    void this.container.offsetWidth;
  }

  // ──────────────────── global offset 추적 (기획서 8.2.3) ────────────────────

  // 문단(pEl) 내에서 targetNode/targetOffset 까지의 누적 문자 수.
  // range.startOffset 을 그대로 쓰면 안 됨 — 노드 로컬 위치일 뿐 (기획서 8.2.3).
  _absoluteCharOffset(pEl, targetNode, targetOffset) {
    const walker = document.createTreeWalker(pEl, NodeFilter.SHOW_TEXT, null);
    let acc = 0;
    let node;
    while ((node = walker.nextNode())) {
      if (node === targetNode) return acc + targetOffset;
      acc += node.nodeValue.length;
    }
    return acc; // 요소 경계인 경우 누적값
  }

  /**
   * 현재 화면 좌상단 픽셀 → global offset (기획서 8.2.3).
   * 실패하거나 비정상이면 percent fallback 으로 globalOffset 역산 (기획서 0.7).
   * @returns {{ globalOffset:number, percent:number }}
   */
  getCurrentGlobalOffset() {
    try {
      const rect = this.container.getBoundingClientRect();
      const x = rect.left + 1;
      const y = rect.top + 1;

      let node = null;
      let nodeOffset = 0;
      if (document.caretRangeFromPoint) {
        const r = document.caretRangeFromPoint(x, y);
        if (r) {
          node = r.startContainer;
          nodeOffset = r.startOffset;
        }
      } else if (document.caretPositionFromPoint) {
        const cp = document.caretPositionFromPoint(x, y);
        if (cp) {
          node = cp.offsetNode;
          nodeOffset = cp.offset;
        }
      }
      if (!node) return this._fallbackOffsetByScroll();

      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      const pEl = el && el.closest('p[data-idx]');
      if (!pEl) return this._fallbackOffsetByScroll();

      const blockIndex = Number(pEl.dataset.idx);
      const paraCharOffset = this._absoluteCharOffset(pEl, node, nodeOffset);
      const globalOffset = this.blocks[blockIndex].start + paraCharOffset;
      const percent = this.totalChars > 0 ? globalOffset / this.totalChars : 0;
      return { globalOffset, percent: clamp01(percent) };
    } catch {
      return this._fallbackOffsetByScroll();
    }
  }

  // caret API 전부 실패 시: 현재 윈도우의 scrollLeft 비율로 근사 (기획서 0.7 fallback)
  _fallbackOffsetByScroll() {
    if (this.windowFrom < 0) return { globalOffset: 0, percent: 0 };
    const winCharStart = this.chunks[this.windowFrom].charStart;
    const winCharEnd = this.chunks[this.windowTo].charEnd;
    const sw = this.container.scrollWidth || 1;
    const ratio = clamp01(this.container.scrollLeft / sw);
    const globalOffset = Math.round(winCharStart + (winCharEnd - winCharStart) * ratio);
    return {
      globalOffset,
      percent: this.totalChars > 0 ? clamp01(globalOffset / this.totalChars) : 0,
    };
  }

  // ──────────────── 단일 진입점 goToOffset (기획서 8.2.4) ────────────────

  // globalOffset → 현재 윈도우 DOM 안의 해당 글자 위치에 Range 생성 (TreeWalker 누적).
  _makeRangeAtGlobalOffset(globalOffset) {
    const blockIndex = findBlockByOffset(this.blocks, globalOffset);
    if (blockIndex < 0) return null;
    const pEl = this.container.querySelector(`p[data-idx="${blockIndex}"]`);
    if (!pEl) return null;

    let localCharOffset = globalOffset - this.blocks[blockIndex].start;
    if (localCharOffset < 0) localCharOffset = 0;

    const walker = document.createTreeWalker(pEl, NodeFilter.SHOW_TEXT, null);
    let acc = 0;
    let node;
    while ((node = walker.nextNode())) {
      const len = node.nodeValue.length;
      if (acc + len >= localCharOffset) {
        const range = document.createRange();
        const off = Math.max(0, Math.min(len, localCharOffset - acc));
        range.setStart(node, off);
        range.setEnd(node, off);
        return range;
      }
      acc += len;
    }
    // 문단 끝을 넘는 경우: 문단 끝에 Range
    const range = document.createRange();
    range.selectNodeContents(pEl);
    range.collapse(false);
    return range;
  }

  /**
   * 모든 위치 이동의 단일 진입점 (기획서 8.2.4).
   * 위치 복원 / 책갈피 / 검색 / 슬라이더 / 페이지 넘김 후 재배치 — 전부 이 함수 경유.
   */
  async goToOffset(globalOffset) {
    if (this.chunks.length === 0) return;
    // ★버그 수정: measure() 전(pageWidth=0)에 진입하면
    //   Math.floor(x / 0) * 0 = NaN 이 scrollLeft 에 들어간다.
    //   정상 흐름은 항상 measure 후 호출하지만, 측정 재시도가 모두
    //   실패하는 극단 경로를 방어한다 (측정 성공 후 재배치됨).
    if (!(this.getPageWidth() > 0)) return;
    globalOffset = Math.max(0, Math.min(this.totalChars, Math.round(globalOffset)));

    // 1. 어느 청크인지 파생
    const chunkIndex = findChunkByOffset(this.chunks, globalOffset);

    // 2. 필요한 윈도우가 DOM 에 없으면 anchor 교체 (reflow 후 column 계산 대기)
    if (!this._isWindowRendered(chunkIndex)) {
      this.renderWindow(chunkIndex);
      await nextTwoFrames(); // 기획서 8.2.4: rAF 2회로 레이아웃 안정화 대기
    }

    // 3~4. globalOffset → Range → 그 글자가 보이는 페이지로 scrollLeft 설정
    const range = this._makeRangeAtGlobalOffset(globalOffset);
    if (!range) {
      // Range 실패 → percent 근사 (기획서 0.7)
      this._scrollToPercentWithinWindow(globalOffset);
    } else {
      const rRect = range.getBoundingClientRect();
      const cRect = this.container.getBoundingClientRect();
      const xInContent = rRect.left - cRect.left + this.container.scrollLeft;
      const pw = this.getPageWidth();
      let target = Math.floor(xInContent / pw) * pw;
      target = Math.max(0, Math.min(this._maxScrollLeft(), target));
      this.container.scrollLeft = target;
    }

    this._emitPosition();
  }

  _maxScrollLeft() {
    const pw = this.getPageWidth();
    const sw = this.container.scrollWidth;
    if (sw <= pw) return 0;
    // column-gap:0 이므로 scrollWidth ≈ pageCount * pw.
    // 소수점 오차 대비 ceil 후 -1px 안쪽으로 (마지막 페이지가 잘리지 않게)
    const pages = Math.max(1, Math.ceil((sw - 1) / pw));
    return (pages - 1) * pw;
  }

  _scrollToPercentWithinWindow(globalOffset) {
    const winStart = this.chunks[this.windowFrom].charStart;
    const winEnd = this.chunks[this.windowTo].charEnd;
    const ratio = clamp01((globalOffset - winStart) / Math.max(1, winEnd - winStart));
    const pw = this.getPageWidth();
    const raw = ratio * this.container.scrollWidth;
    this.container.scrollLeft = Math.min(
      this._maxScrollLeft(),
      Math.floor(raw / pw) * pw
    );
  }

  _emitPosition() {
    if (this._onPositionChange) {
      const { globalOffset, percent } = this.getCurrentGlobalOffset();
      this._onPositionChange(globalOffset, percent);
    }
  }

  // ──────────────── 페이지 넘김 + 윈도우 경계 (기획서 8.2.5) ────────────────

  // 현재 윈도우 안에서 페이지 단위 위치
  _currentPageIndex() {
    return Math.round(this.container.scrollLeft / this.getPageWidth());
  }
  _lastPageIndex() {
    return Math.round(this._maxScrollLeft() / this.getPageWidth());
  }

  // 현재 화면(현재 페이지)에 보이는 마지막 글자의 다음 global offset.
  // 페이지 넘김의 본질 = "현재 페이지의 끝 다음 글자로 이동". 윈도우 경계
  // 여부와 무관하게 이 offset 으로 goToOffset 하면, 그 글자가 다른 청크에
  // 있어도 단일 진입점이 윈도우 교체까지 처리한다 (기획서 0.6, 8.2.4).
  // 구하는 법: 현재 페이지 영역의 우하단 근처를 caret 으로 읽어 그 글자
  // offset 을 얻고 +1. 실패 시 null.
  _offsetAfterCurrentPage() {
    try {
      const rect = this.container.getBoundingClientRect();
      const pw = this.getPageWidth();
      // 현재 페이지 영역의 오른쪽 끝, 세로로는 거의 바닥. 마지막 글자에
      // 최대한 가까운 지점을 caret 으로 찍는다.
      const x = rect.left + pw - 2;
      const y = rect.top + rect.height - 2;
      let node = null;
      let nodeOffset = 0;
      if (document.caretRangeFromPoint) {
        const r = document.caretRangeFromPoint(x, y);
        if (r) {
          node = r.startContainer;
          nodeOffset = r.startOffset;
        }
      } else if (document.caretPositionFromPoint) {
        const cp = document.caretPositionFromPoint(x, y);
        if (cp) {
          node = cp.offsetNode;
          nodeOffset = cp.offset;
        }
      }
      if (!node) return null;
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      const pEl = el && el.closest('p[data-idx]');
      if (!pEl) return null;
      const blockIndex = Number(pEl.dataset.idx);
      const paraCharOffset = this._absoluteCharOffset(pEl, node, nodeOffset);
      return this.blocks[blockIndex].start + paraCharOffset + 1;
    } catch {
      return null;
    }
  }

  async nextPage() {
    const pw = this.getPageWidth();
    if (!(pw > 0) || this.windowFrom < 0 || this.windowTo < 0) {
      // measure/렌더 전 호출 방어 (정상 흐름에선 발생 안 함)
      this._emitPosition();
      return;
    }

    // 현재 윈도우 DOM 안에 아직 넘길 페이지가 남아 있으면 scrollLeft 만 이동.
    if (this._currentPageIndex() < this._lastPageIndex()) {
      this.container.scrollLeft = Math.min(
        this._maxScrollLeft(),
        this.container.scrollLeft + pw
      );
      this._emitPosition();
      return;
    }

    // ── 윈도우의 마지막 페이지에 도달 ──
    // 다음 청크가 없으면(윈도우가 마지막 청크를 포함) 책의 진짜 끝.
    if (this.windowTo >= this.chunks.length - 1) {
      this._emitPosition();
      return;
    }

    // ★버그 수정 (윈도우 경계 넘김):
    //   이전 코드는 goToOffset(winEnd)(윈도우 전체 끝 청크 경계)로
    //   점프해, 새 윈도우에서 그 글자가 현재 페이지에 정렬되어 실질
    //   전진이 0 → 다청크 책이 앞부분에서 멈췄다.
    //   올바른 동작: "현재 페이지의 끝 다음 글자" offset 으로 이동.
    //   그 글자가 다음 청크에 있으면 goToOffset 이 윈도우를 교체하고
    //   그 글자가 보이는 페이지(= 다음 페이지)로 정렬한다.
    const here = this.getCurrentGlobalOffset().globalOffset;
    let target = this._offsetAfterCurrentPage();

    if (target == null || !Number.isFinite(target) || target <= here) {
      // caret 으로 페이지 끝 글자를 못 읽었으면, 다음 청크 시작으로
      // 폴백 (최소한 한 청크는 전진하며 끊김 없이 진행).
      target = this.chunks[this.windowTo + 1].charStart;
      if (target <= here) target = here + 1;
    }

    if (target >= this.totalChars) {
      // 다음 글자가 책 끝을 넘음 → 마지막 글자가 보이도록 끝으로.
      await this.goToOffset(this.totalChars - 1);
      this._emitPosition();
      return;
    }

    await this.goToOffset(target);
  }

  async prevPage() {
    const pw = this.getPageWidth();
    if (this._currentPageIndex() > 0) {
      this.container.scrollLeft = Math.max(0, this.container.scrollLeft - pw);
      this._emitPosition();
      return;
    }
    // 윈도우 첫 페이지 → 앞 청크 포함 후 복원 (기획서 8.2.5 대칭)
    const winStart = this.chunks[this.windowFrom].charStart;
    if (winStart <= 0) {
      this._emitPosition();
      return;
    }
    await this.goToOffset(winStart - 1);
  }

  destroy() {
    this.container.innerHTML = '';
    this.blocks = [];
    this.chunks = [];
    this._onPositionChange = null;
  }
}

function clamp01(x) {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function nextTwoFrames() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

// XSS·HTML 깨짐 방지. block.text 는 사용자 파일이므로 이스케이프 필수.
function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
